import React, { useEffect, useState } from 'react';
import { Box, Container, useMediaQuery, useTheme } from '@mui/material';
import LandingHeader from './LandingHeader';
import { HeroSection, FeaturesSection } from '../Landing';
import { EmailVerificationStatus } from '../Auth';
import EmailVerificationGraceWarning from '../Auth/EmailVerificationGraceWarning';
import EmailVerificationBlockModal from '../Auth/EmailVerificationBlockModal';
import { AlertDialog, LoadingSpinner } from '../common';
import { HEADER_HEIGHT } from '../../constants';
import { useTheme as useThemeDeprecated } from '@mui/material/styles';
import { useAuth } from '../../hooks/useAuth';
import { useEmailVerificationGrace } from '../../hooks/useAuth/useEmailVerificationGrace';
import { useFeatureBlock } from '../../hooks/useAuth/useFeatureBlock';
import { useTranslation } from 'react-i18next';

const LandingLayout = ({
  // Mobile detection
  isMobile,
  // Drawer state
  setDrawerOpen,
  // Hero section props
  inputUrl,
  setInputUrl,
  inputTitle,
  loading,
  error,
  handleAnalyzeClick,
  fetchYoutubeTitle,
  // Features section props
  isLoggedIn,
  userId,
  setLoginDialogOpen,
  history
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobileBreakpoint = useMediaQuery(theme.breakpoints.down('md'));
  const { user, resendEmailVerification, isResending, authLoading } = useAuth();
  
  const { 
    isGraceExpired, 
    isInGracePeriod, 
    formatTimeRemaining,
    refreshGraceInfo 
  } = useEmailVerificationGrace(user);
  
  const { 
    blockModalOpen, 
    checkAndExecute, 
    closeBlockModal,
    openBlockModal 
  } = useFeatureBlock(user);

  // 재발송 성공 모달 상태
  const [resendSuccessModal, setResendSuccessModal] = useState({
    open: false,
    message: ''
  });

  // 회원가입 성공 모달 상태 (재발송 모달과 동일한 방식)
  const [signupSuccessModal, setSignupSuccessModal] = useState({
    open: false,
    message: '',
    title: ''
  });

  // 이메일 인증 완료 모달 상태
  const [emailVerificationSuccessModal, setEmailVerificationSuccessModal] = useState({
    open: false,
    message: '',
    title: ''
  });

  // 재발송 함수 (랜딩페이지용 - 성공시 모달 표시)
  const handleResendVerification = async (user) => {
    const success = await resendEmailVerification(user);
    if (success) {
      setResendSuccessModal({
        open: true,
        message: t('emailVerification.resendSuccess')
      });
    }
  };

  // 재발송 성공 모달 닫기
  const closeResendSuccessModal = () => {
    setResendSuccessModal({
      open: false,
      message: ''
    });
  };

  // 회원가입 성공 모달 닫기
  const closeSignupSuccessModal = () => {
    setSignupSuccessModal({
      open: false,
      message: '',
      title: ''
    });
  };

  // 이메일 인증 완료 모달 닫기
  const closeEmailVerificationSuccessModal = () => {
    setEmailVerificationSuccessModal({
      open: false,
      message: '',
      title: ''
    });
  };

  // sessionStorage에서 모달 표시 플래그 체크
  useEffect(() => {
    const shouldShowModal = sessionStorage.getItem('showBlockModal');
    if (shouldShowModal === 'true' && isGraceExpired) {
      // 플래그 제거
      sessionStorage.removeItem('showBlockModal');
      // 모달 표시
      openBlockModal();
    }
  }, [isGraceExpired, openBlockModal]);

  // 이메일 인증 완료 플래그 감지
  useEffect(() => {
    const emailVerificationSuccess = sessionStorage.getItem('emailVerificationSuccess');
    if (emailVerificationSuccess === 'true') {
      // 플래그 제거
      sessionStorage.removeItem('emailVerificationSuccess');
      
      // 유예 기간 정보 즉시 새로고침 (인증 완료 상태 반영)
      setTimeout(() => {
        refreshGraceInfo();
      }, 100); // 빠른 새로고침
      
      // 추가로 1초 후에도 한번 더 새로고침 (확실한 상태 동기화)
      setTimeout(() => {
        refreshGraceInfo();
      }, 1000);
      
      // 인증 완료 모달 표시
      setEmailVerificationSuccessModal({
        open: true,
        title: t('emailVerification.success.title'),
        message: t('emailVerification.success.message')
      });
    }
  }, [t, refreshGraceInfo]);

  // 회원가입 성공 감지 (전역 이벤트 리스너)
  useEffect(() => {
    const handleSignupSuccess = (event) => {
      const eventDetail = event.detail;
      
      // 회원가입 성공 후 유예 기간 정보 새로고침 (새로고침 없이 알림 표시)
      setTimeout(() => {
        refreshGraceInfo();
      }, 1000); // 1초 후 상태 새로고침
      
      // 계정 연동인 경우와 일반 회원가입인 경우 구분
      if (eventDetail && eventDetail.isAccountLinked) {
        // 계정 연동 성공
        setSignupSuccessModal({
          open: true,
          title: t('auth.accountLinkTitle'),
          message: t('auth.accountLinkMessage', { provider: eventDetail.provider })
        });
      } else {
        // 일반 회원가입 성공
        setSignupSuccessModal({
          open: true,
          title: t('auth.signupSuccessTitle'),
          message: t('auth.signupSuccessMessage')
        });
      }
    };

    // 전역 이벤트 리스너 등록
    window.addEventListener('signup-success', handleSignupSuccess);

    return () => {
      window.removeEventListener('signup-success', handleSignupSuccess);
    };
  }, [t, refreshGraceInfo]);

  // 모바일 헤더 높이 계산
  const getMobileHeaderHeight = () => {
    if (isMobileBreakpoint) {
      return 56; // MobileHeaderTop의 높이
    }
    return HEADER_HEIGHT;
  };

  return (
    <Box 
      component="main" 
      className={isMobile ? '' : ''}
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fff',
        overflowY: 'auto',
        overflowX: 'hidden'
      }}
    >
      <LandingHeader 
        setDrawerOpen={setDrawerOpen} 
        className={isMobile ? 'mobile-landing-header' : ''}
      />
      
      <Container 
        maxWidth="lg" 
        className={isMobile ? 'mobile-landing-content' : ''}
        sx={{ 
          flex: 1, 
          mt: 4, 
          // 헤더 높이 + iOS Safe Area 상단 - 모바일에서 새로운 높이 적용
          pt: isMobileBreakpoint ? 
            `calc(${getMobileHeaderHeight()}px + env(safe-area-inset-top))` : 
            `${HEADER_HEIGHT}px`
        }}
      >
        {/* 유예 기간 경고 메시지 */}
        {isInGracePeriod && (
          <EmailVerificationGraceWarning
            timeRemaining={formatTimeRemaining}
            showTimeRemaining={true}
            onResendVerification={() => handleResendVerification(user)}
            isResending={isResending}
          />
        )}

        {/* 유예 기간 만료 후 경고 메시지 (시간 표시 없음) */}
        {isGraceExpired && (
          <EmailVerificationGraceWarning
            timeRemaining={null}
            showTimeRemaining={false}
            onResendVerification={() => handleResendVerification(user)}
            isResending={isResending}
          />
        )}

        {/* 기존 이메일 인증 상태 표시 - OAuth 사용자용 */}
        {isLoggedIn && user && !isInGracePeriod && !isGraceExpired && (
          <EmailVerificationStatus
            user={user}
            onResendVerification={() => handleResendVerification(user)}
            isResending={isResending}
          />
        )}
        
        {/* Hero Section - 메인 YouTube 입력 영역 */}
        <HeroSection
          inputUrl={inputUrl}
          setInputUrl={setInputUrl}
          inputTitle={inputTitle}
          loading={loading}
          error={error}
          handleAnalyzeClick={(url) => checkAndExecute(() => handleAnalyzeClick(url))}
          fetchYoutubeTitle={fetchYoutubeTitle}
          isMobile={isMobile}
        />
        
        {/* Features Section - 앱스토어 버튼, 인기 비디오, 최근 비디오 */}
        <FeaturesSection
          isLoggedIn={isLoggedIn}
          userId={userId}
          setLoginDialogOpen={setLoginDialogOpen}
          history={history}
          checkAndExecute={checkAndExecute}
        />
      </Container>

      {/* 기능 차단 모달 */}
      <EmailVerificationBlockModal
        open={blockModalOpen}
        onClose={closeBlockModal}
        userEmail={user?.email}
        onResendVerification={() => handleResendVerification(user)}
        isResending={isResending}
      />

      {/* 재발송 성공 모달 */}
      <AlertDialog
        open={resendSuccessModal.open}
        onClose={closeResendSuccessModal}
        type="success"
        title=""
        message={resendSuccessModal.message}
        confirmText={t('common.confirm')}
      />

      {/* 회원가입 성공 모달 (재발송 모달과 동일한 방식) */}
      <AlertDialog
        open={signupSuccessModal.open}
        onClose={closeSignupSuccessModal}
        type="success"
        title={signupSuccessModal.title}
        message={signupSuccessModal.message}
        confirmText={t('common.confirm')}
      />

      {/* 이메일 인증 완료 모달 */}
      <AlertDialog
        open={emailVerificationSuccessModal.open}
        onClose={closeEmailVerificationSuccessModal}
        type="success"
        title={emailVerificationSuccessModal.title}
        message={emailVerificationSuccessModal.message}
        confirmText={t('common.confirm')}
      />

      {/* 로그인/회원가입 로딩 스피너 (본문에 표시) */}
      <LoadingSpinner 
        open={authLoading} 
        message={t('common.processing', '로그인 처리 중...')} 
      />
    </Box>
  );
};

export default LandingLayout; 